import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Footer from '../offer-package/footer';
import Header from '../offer-package/header';
import { ISpecialPackageDto } from '../model';
import { SpecialPackageApi } from '../services';
import { PriceRender, StyledButton } from '../ui';
import SpecialPackageOccupancyPicker from './occupancy-picker';
import {
  getAvailableSpecialPackageOccupancies,
  getDefaultSpecialPackageOccupancy,
  getSpecialPackagePriceForOccupancy,
} from './occupancy';
import {
  getSpecialPackageDescription,
  getSpecialPackageTitle,
} from './text';

export function SpecialListPage() {
  const { t, i18n } = useTranslation('offer-package');
  const navigate = useNavigate();
  const specialPackageApi = new SpecialPackageApi();
  const [specialPackages, setSpecialPackages] =
    useState<ISpecialPackageDto[]>();
  const [selectedOccupancyById, setSelectedOccupancyById] = useState<
    Record<number, number>
  >({});

  useEffect(() => {
    specialPackageApi
      .getActive()
      .then((data) => setSpecialPackages(data))
      .finally();
  }, []);

  return (
    <div>
      <Header></Header>

      <main className="container max-w-5xl px-2 sm:px-0 pb-24">
        <section className="py-10 md:py-16">
          <div className="max-w-3xl space-y-4">
            <p className="text-sm uppercase tracking-[0.3em] text-brand-green-700">
              {t('special.list.eyebrow')}
            </p>
            <h1 className="font-serif text-4xl text-brand-cashmere-900">
              {t('special.list.title')}
            </h1>
            <p className="text-lg text-brand-cashmere-800">
              {t('special.list.description')}
            </p>
          </div>
        </section>

        {!specialPackages && <p>{t('special.shared.loading')}</p>}

        {specialPackages && specialPackages.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-slate-700">
            {t('special.list.empty')}
          </div>
        )}

        {specialPackages && specialPackages.length > 0 && (
          <div className="grid gap-6">
            {specialPackages.map((item) => {
              const availableOccupancies =
                getAvailableSpecialPackageOccupancies(item);
              const selectedOccupancy =
                selectedOccupancyById[item.id] ??
                getDefaultSpecialPackageOccupancy(item);
              const packagePrice = getSpecialPackagePriceForOccupancy(
                item,
                selectedOccupancy
              );
              const packageTitle = getSpecialPackageTitle(item, i18n.language);
              const packageDescription = getSpecialPackageDescription(
                item,
                i18n.language
              );

              return (
                <article
                  key={item.id}
                  className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
                >
                  <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <h2 className="font-serif text-3xl text-brand-cashmere-900">
                          {packageTitle}
                        </h2>
                        {packageDescription && (
                          <p className="max-w-3xl whitespace-pre-line text-brand-cashmere-800">
                            {packageDescription}
                          </p>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-3 text-sm text-slate-700">
                        <span className="rounded-full bg-slate-100 px-4 py-2">
                          {t('special.shared.arrival')}:{' '}
                          {format(item.arrival, 'dd.MM.yyyy')}
                        </span>
                        <span className="rounded-full bg-slate-100 px-4 py-2">
                          {t('special.shared.departure')}:{' '}
                          {format(item.departure, 'dd.MM.yyyy')}
                        </span>

                        {availableOccupancies.length === 1 && (
                          <span className="rounded-full bg-slate-100 px-4 py-2">
                            {t('special.shared.occupancy')}:{' '}
                            {t(
                              selectedOccupancy === 1
                                ? 'special.shared.one-person'
                                : 'special.shared.two-persons'
                            )}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex min-w-60 md:min-w-72 flex-col gap-4 rounded-xl bg-brand-cashmere-50 p-5">
                      {availableOccupancies.length > 1 && (
                        <div className="space-y-2">
                          <p className="text-sm text-slate-600">
                            {t('special.shared.occupancy')}
                          </p>
                          <SpecialPackageOccupancyPicker
                            availableOccupancies={availableOccupancies}
                            selectedOccupancy={selectedOccupancy}
                            onChange={(occupancy) =>
                              setSelectedOccupancyById((previous) => ({
                                ...previous,
                                [item.id]: occupancy,
                              }))
                            }
                          ></SpecialPackageOccupancyPicker>
                        </div>
                      )}

                      <div>
                        <p className="text-sm text-slate-600">
                          {t('special.shared.reduced-total')}
                        </p>
                        <p className="mt-1 text-2xl font-semibold text-slate-900">
                          {packagePrice === null ? (
                            t('special.shared.price-on-request')
                          ) : (
                            <PriceRender price={packagePrice}></PriceRender>
                          )}
                        </p>
                      </div>

                      <StyledButton
                        type="button"
                        design="primary"
                        onClick={() => {
                          window.scrollTo(0, 0);
                          navigate(`/${i18n.language}/special/${item.id}`, {
                            state: {
                              fromSpecialList: true,
                              selectedOccupancy,
                            },
                          });
                        }}
                      >
                        {t('special.list.action')}
                      </StyledButton>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>

      <Footer></Footer>
    </div>
  );
}

export default SpecialListPage;
